import { Design } from "../../../../core/models/entities/Design";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import FunctionalList from "../../../components/GenericTable/FunctionalList";
import {
  DesignClickCallback,
  useFunctionalListDesignViewModel,
} from "../vmHooks/useFunctionalListDesignViewModel";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Style } from "../../../../core/models/entities/Style";
import { TransferMethod } from "../../../../core/models/entities/LogoOptions";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";
import { useApprovalDialogByRole } from "../../../components/Dialogs/useApprovalDialogByRole";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../../enviroment";
import { useListSearchFeedback } from "../../../shared/hooks/useListSearchFeedback";
import { listSearchTexts } from "../../../shared/components/list-search-status";

interface WrapperProps {
  onSelectDesign: (design: Design) => void;
  lastPage: number;
  query: PageQuery;
  customers: Organization[];
  isPageLoading: boolean;
  // keepPreviousData indicators from useAllDesigns: while isFetching &&
  // isPreviousData the shown rows belong to the previous query and
  // FunctionalList dims them (no full spinner).
  isFetching?: boolean;
  isPreviousData?: boolean;
  // GetAllDesigns' totalCount for the search status line (flag
  // designSearchFeedbackEnabled); undefined = "N on this page" fallback.
  totalCount?: number;
  designs?: Design[];
  vendors: Organization[] | undefined;
  queryChanger: QueryChanger;
  manufacturers: Manufacturer[];
  styles: Style[];
  methods: TransferMethod[];
  onShowRejectionNote: (design: Design) => void;
  onApprove: DesignClickCallback;
  onReject: DesignClickCallback;
  updatingDesignId?: number;
  loadingPrices?: boolean;
  // Optional paginator hover-prefetch (forwarded to FunctionalList).
  onPrefetchPage?: (pageIndex: number) => void | Promise<void>;
}

// designList.searchSearching … designList.searchClear (all 10 locale files).
const DESIGN_SEARCH_TEXTS = listSearchTexts("designList");

export function DesignListWrapper({
  onSelectDesign,
  lastPage,
  query,
  customers,
  isPageLoading,
  isFetching,
  isPreviousData,
  totalCount,
  designs,
  vendors,
  queryChanger,
  methods,
  styles,
  manufacturers,
  onShowRejectionNote,
  onApprove,
  onReject,
  updatingDesignId,
  loadingPrices,
  onPrefetchPage,
}: WrapperProps) {
  const { profile } = useProfileContext();
  const { t } = useTranslation();
  const { handleInitiateApproval, handleApprovalSkip, handleSkipAddToCart } =
    useDesignActionHandler();
  const { component, start: openInitApproval } =
    useApprovalDialogByRole<Design>({
      role: profile?.userOrganizationInformations.role!,
      subject: t("design"),
      onSkip: (entity) => handleApprovalSkip(entity).then(),
      onOrder: (entity) => handleSkipAddToCart(entity, 10).then(),
      onInitApproval: (entity, email) =>
        handleInitiateApproval(entity, { email }).then(),
    });

  const vm = useFunctionalListDesignViewModel(
    onSelectDesign,
    onApprove,
    onReject,
    lastPage,
    query,
    customers,
    isPageLoading,
    vendors,
    queryChanger,
    manufacturers,
    styles,
    methods,
    onShowRejectionNote,
    // openInitApproval,
    designs,
    updatingDesignId,
    loadingPrices,
    isFetching,
    isPreviousData
  );

  // Search feedback (flag designSearchFeedbackEnabled): spinner in the
  // search field, status line and progress bar - derived from the list's own
  // react-query state, 0 calls; rows, prices, stock and status untouched.
  // Flag off = {} spread below, so FunctionalList sees exactly today's props.
  const searchFeedback = useListSearchFeedback({
    enabled: enviroment.designSearchFeedbackEnabled,
    searchTerm: query.searchTerm,
    isFetching: !!isFetching,
    isPreviousData: !!isPreviousData,
    totalCount,
    pageResultCount: designs?.length ?? 0,
    onClear: () => queryChanger.handleSearchQueryChange({ searchTerm: "" }),
    texts: DESIGN_SEARCH_TEXTS,
    statusTestId: "design-search-status",
  });

  // folderBar is not part of TableData - it renders ABOVE the list box
  // (breadcrumb + folder cards; vendor/admin only, see the view model).
  const { folderBar, ...tableData } = vm;

  return (
    <>
      {component}
      {folderBar}
      <FunctionalList<Design>
        {...tableData}
        onPrefetchPage={onPrefetchPage}
        {...searchFeedback}
      />
    </>
  );
}
